'use client';

import { useState, useEffect } from 'react';
import { getDevToolsStorage, applyQueryParamVersions } from '../utils';

export interface MfeVersionsType {
	auth: { version: string };
	garage: { version: string };
	settings: { version: string };
}

const DEFAULT_MFE_VERSIONS: MfeVersionsType = {
	auth: { version: '' },
	garage: { version: '' },
	settings: { version: '' }
};

const getInitialMfeVersions = (): MfeVersionsType => {
	// Avoid calling getDevToolsStorage during SSR
	if (typeof window === 'undefined') {
		return DEFAULT_MFE_VERSIONS;
	}

	// Apply query param versions first (they take precedence)
	applyQueryParamVersions();

	const savedDevTools = getDevToolsStorage();
	return {
		auth: { version: savedDevTools?.auth.version || '' },
		garage: { version: savedDevTools?.garage.version || '' },
		settings: { version: savedDevTools?.settings.version || '' }
	};
};

export const useMfeVersions = () => {
	const [mfeVersions, setMfeVersions] = useState<MfeVersionsType>(getInitialMfeVersions);

	// Load saved versions after hydration
	useEffect(() => {
		// Apply query param versions first (they take precedence)
		applyQueryParamVersions();

		const savedVersions = getDevToolsStorage();
		if (savedVersions) {
			setMfeVersions(savedVersions);
		}
	}, []);

	const updateMfeVersion = (mfe: keyof MfeVersionsType, version: string) => {
		setMfeVersions((prevVersions) => ({
			...prevVersions,
			[mfe]: { ...prevVersions[mfe], version }
		}));
	};

	const updateAuthVersion = (version: string) => {
		updateMfeVersion('auth', version);
	};

	const updateGarageVersion = (version: string) => {
		updateMfeVersion('garage', version);
	};

	const updateSettingsVersion = (version: string) => {
		updateMfeVersion('settings', version);
	};

	return {
		mfeVersions,
		updateAuthVersion,
		updateGarageVersion,
		updateSettingsVersion
	};
};

export default useMfeVersions;
